/**
 * The menu-validation round trip: a document out, `MenuProblem`s back.
 *
 * WHY THERE IS A PROTOCOL HERE AT ALL, rather than a TypeScript validator. FabricMenus'
 * `MenuAPI.validateJson` is the ONLY thing that decides whether a menu document is legal. It
 * knows the item types the installed build supports, the actions every loaded extension
 * registered, the pattern rules, and the twenty other things a menu file can get wrong - and
 * it knows them because it IS the engine that will render the menu. A second validator
 * written in the browser would be a weaker opinion that disagrees with the real one, and the
 * disagreement would land on an admin as "the editor said it was fine and the server refused
 * it". So the browser sends the text and renders the answer, and holds no rules of its own.
 *
 * THE ANSWER IS FIELD-SHAPED, NOT LOG-SHAPED. Every problem carries an RFC 6901 JSON pointer
 * into the document it complains about - `/pattern/2`, `/items/3/actionList/MOUSE_LEFT/0` -
 * which is precisely what a form editor needs to put the complaint on the control that caused
 * it. That structure survives from `MenuProblem.path()` in the mod, through
 * `MenuProblemView.pointer()` (the quarantine's outward form, §28), to here, and only becomes
 * a sentence at the last moment. Flattening it into prose anywhere along the way would throw
 * away the only part a UI can act on.
 *
 * WHO ANSWERS. The validator runs inside the game server, which never opens an inbound port,
 * so the sidecar cannot simply proxy to it: the route below is the seam, and what stands
 * behind it depends on the deployment.
 *
 *   - In `vite dev` / `vite preview` a stand-in plays the mod's part
 *     (`app/vite-plugin-dev-menuvalidator.ts`), exactly as the capture fixture plays a live
 *     player's part and the registry fixture plays a booted server's.
 *   - In a deployment the sidecar answers {@link MENU_VALIDATOR_UNAVAILABLE} until the mod
 *     side of the exchange lands, and the editor says so in as many words.
 *
 * A DEPLOYMENT THAT CANNOT VALIDATE MUST STILL EDIT. Every non-answer degrades to "no
 * verdict", never to "invalid" and never to a blocked save - the same rule the registry dump
 * and the session bundle already follow. An editor that refused to work because it could not
 * reach a validator would be worse than one that never had it.
 */

/** Wire version of the exchange. Bumped when a field's meaning changes, not when one is added. */
export const MENU_CHECK_VERSION = 1;

/**
 * Where the browser asks. Session-scoped rather than global: a validation is only meaningful
 * against the server that pushed the bundle, and the code is the capability that names it.
 */
export function menuCheckPath(code: string): string {
  return `/v1/session/${code}/menu-check`;
}

/**
 * Hard cap on one document. FabricMenus menus are 1-6 rows of 9 slots with a stack each; the
 * largest shipped layout is under 8 KB, so this is two orders of headroom and still small
 * enough that a runaway paste cannot become a megabyte per keystroke.
 */
export const MENU_CHECK_MAX_BYTES = 512 * 1024;

/**
 * One document to check.
 *
 * `menuId` travels because it is not derivable from the text: a menu's id IS its file name
 * and it is case-sensitive, so the validator's complaint about a cross-menu reference needs
 * to know which file it is looking at.
 */
export interface MenuCheckRequest {
  readonly version: typeof MENU_CHECK_VERSION;
  readonly menuId: string;
  /** The document as TEXT, verbatim - never a re-serialised object. See the file header. */
  readonly json: string;
}

/**
 * One complaint, as `MenuProblem` made it.
 *
 * `pointer` is RFC 6901 and `""` means "the document as a whole". `error` is the API's own
 * `Severity`: an error refuses the document at apply time, a warning does not - and the
 * warning is often the more interesting one, because the case this whole surface exists to
 * prevent (an `actionList` on an item type that never fires one) is reported as a WARNING and
 * then silently does nothing forever.
 */
export interface MenuCheckProblem {
  readonly pointer: string;
  readonly message: string;
  readonly error: boolean;
}

export interface MenuCheckReport {
  readonly version: typeof MENU_CHECK_VERSION;
  readonly menuId: string;
  readonly problems: readonly MenuCheckProblem[];
  /**
   * What produced the verdict, shown to the admin so a stand-in is never mistaken for the
   * real engine. `fabricmenus` is `MenuAPI.validateJson` itself; anything else is a stand-in
   * and the editor labels it as one.
   */
  readonly validator: string;
}

/** The sidecar's refusal when nothing behind it can validate. Not an error the admin caused. */
export const MENU_VALIDATOR_UNAVAILABLE = 'menu_validator_unavailable';

export interface MenuCheckError {
  readonly error: typeof MENU_VALIDATOR_UNAVAILABLE | 'bad_code' | 'invalid_request' | 'payload_too_large' | 'method_not_allowed';
  /** One sentence an admin can act on, or at least understand. */
  readonly detail: string;
}

export function isMenuCheckReport(value: unknown): value is MenuCheckReport {
  if (typeof value !== 'object' || value === null) return false;
  const report = value as Partial<MenuCheckReport>;
  return (
    report.version === MENU_CHECK_VERSION &&
    typeof report.menuId === 'string' &&
    typeof report.validator === 'string' &&
    Array.isArray(report.problems) &&
    report.problems.every(isMenuCheckProblem)
  );
}

function isMenuCheckProblem(value: unknown): value is MenuCheckProblem {
  if (typeof value !== 'object' || value === null) return false;
  const problem = value as Partial<MenuCheckProblem>;
  return typeof problem.pointer === 'string' && typeof problem.message === 'string' && typeof problem.error === 'boolean';
}

/**
 * Structural check of a request, used by whatever answers one.
 *
 * Deliberately says nothing about whether the JSON parses: a document mid-edit routinely does
 * not, and "your JSON is broken" is a verdict the validator should give as a problem with a
 * pointer, not a 400 with no field attached.
 */
export function isMenuCheckRequest(value: unknown): value is MenuCheckRequest {
  if (typeof value !== 'object' || value === null) return false;
  const request = value as Partial<MenuCheckRequest>;
  return request.version === MENU_CHECK_VERSION && typeof request.menuId === 'string' && typeof request.json === 'string';
}
